import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/stores'

export function useAuth() {
  const navigate = useNavigate()
  const {
    session,
    user,
    organization,
    isLoading,
    isInitialized,
    setIsLoading,
    setIsInitialized,
    reset,
  } = useAuthStore()

  // NOTE: Auth initialization and onAuthStateChange are handled
  // solely by AuthProvider. This hook only provides action functions
  // (signIn, signOut, etc.) and reads state from the store.

  const signIn = useCallback(async (email: string, password: string) => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      return { data, error: null }
    } catch (error) {
      return { data: null, error: error as Error }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const signUp = useCallback(async (
    email: string,
    password: string,
    fullName: string,
    organizationName?: string,
    vendorInviteCode?: string
  ) => {
    setIsLoading(true)
    try {
      // Sign up user - organization and user profile are created via database trigger
      const metadata: Record<string, string> = {
        full_name: fullName,
        organization_name: organizationName || `${fullName}'s Organization`,
      }
      if (vendorInviteCode) {
        metadata.vendor_invite_code = vendorInviteCode
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata },
      })

      if (authError) throw authError

      return { data: authData, error: null }
    } catch (error) {
      return { data: null, error: error as Error }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      // Don't call reset() here — let the onAuthStateChange handler
      // in AuthProvider handle it, which properly resets state AND
      // sets isInitialized=true so the app doesn't get stuck on Loading.
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
      // On error, still navigate to login and reset
      reset()
      setIsInitialized(true)
      navigate('/login')
    }
  }, [navigate, reset, setIsInitialized])

  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })
      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }, [])

  return {
    session,
    user,
    organization,
    isLoading,
    isInitialized,
    isAuthenticated: !!session && !!user,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    refreshUserData: async () => {
      // Trigger a re-fetch by signing in again with the current session
      // AuthProvider's onAuthStateChange will handle fetching user data
      if (session?.user) {
        const { data } = await supabase.auth.getSession()
        if (data.session) {
          // Force AuthProvider to re-fetch by getting a fresh session
          await supabase.auth.refreshSession()
        }
      }
    },
  }
}
